export interface Lesson {
  id: string;
  title: string;
  durationMin: number;
}

export interface Module {
  id: string;
  number: number;
  title: string;
  description: string;
  durationLabel: string;
  durationMin: number;
  access: 'free' | 'pro';
  lessons: Lesson[];
}

function lessons(moduleId: string, titles: Array<[string, number]>): Lesson[] {
  return titles.map(([title, durationMin], i) => ({
    id: `${moduleId}-l${i + 1}`,
    title,
    durationMin,
  }));
}

function durationLabel(totalMin: number): string {
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hr === 0) return `${min} min`;
  return min === 0 ? `${hr} hr` : `${hr} hr ${min} min`;
}

function buildModule(
  id: string,
  number: number,
  title: string,
  description: string,
  access: 'free' | 'pro',
  lessonSpecs: Array<[string, number]>,
): Module {
  const durationMin = lessonSpecs.reduce((sum, [, m]) => sum + m, 0);
  return {
    id,
    number,
    title,
    description,
    access,
    durationMin,
    durationLabel: durationLabel(durationMin),
    lessons: lessons(id, lessonSpecs),
  };
}

// Invented for this demo, per requirements §7 — structure is designed so a
// real licensor curriculum can drop into the same shape later. Module 1's
// exact lesson breakdown and durations match the source design mock
// verbatim; modules 2-8 have authored lesson titles (the mock only gave
// per-module totals plus one fully-detailed example, module 4) but their
// per-module lesson COUNT and total duration match requirements §7's table
// exactly.
export const CURRICULUM: Module[] = [
  buildModule(
    'm1',
    1,
    'How a scam actually works',
    "Every scam you will ever receive is running the same play. This module takes one apart end to end — what the sender actually needs from you, the four levers they pull to get it, and the single point where the whole thing falls over if you know what to do.",
    'free',
    [
      ['Why smart people get caught', 6],
      ['The four levers every scam pulls', 8],
      ['Anatomy of a real scam, start to finish', 9],
      ['The habit that stops most of them', 6],
      ['Knowledge check', 3],
    ],
  ),
  buildModule(
    'm2',
    2,
    'Phishing, texts, and fake invoices',
    'Reading a sender address, a link, and a QR code. Plus the invoice that looks exactly like the last twelve.',
    'pro',
    [
      ['Reading a sender address you were never taught to check', 8],
      ['Links, QR codes, and where they actually lead', 8],
      ['The invoice that looks exactly like the last twelve', 8],
      ['Text and voicemail scams follow the same script', 7],
      ['What to do when a message almost got you', 7],
    ],
  ),
  buildModule(
    'm3',
    3,
    'Impersonation and the urgent request',
    "The boss who needs a favour, the vendor whose account changed, and why gift cards are still everywhere.",
    'pro',
    [
      ['The boss who needs a favor, right now', 9],
      ["When a vendor's account \"changes\"", 9],
      ['Gift cards, wire transfers, and other one-way doors', 9],
      ['Verifying identity without being the awkward one', 8],
    ],
  ),
  buildModule(
    'm4',
    4,
    'Wire fraud and payment redirection',
    'The single most expensive scam a small business faces, and the one with the shortest window to fix. How closing and escrow fraud gets set up, why "our banking details have changed" is the most dangerous sentence in your inbox, and the two-minute habit that stops nearly all of it.',
    'pro',
    [
      ['How closing and escrow fraud is built', 12],
      ['"Our banking details have changed"', 11],
      ['Callback verification that actually works', 10],
      ['Two people, every payment', 9],
    ],
  ),
  buildModule(
    'm5',
    5,
    'Voices, faces, and AI',
    'Cloned voices, deepfake video on a "quick call", and fake meeting links. What still gives them away.',
    'pro',
    [
      ['Cloned voices on a "quick call"', 9],
      ['Deepfake video in a real-looking meeting', 9],
      ['Fake meeting links and calendar invites', 9],
      ['What still gives an AI impersonation away', 9],
    ],
  ),
  buildModule(
    'm6',
    6,
    'Locking down devices and accounts',
    "Passwords you don't have to remember, which kind of MFA actually stops attackers, and public Wi-Fi honestly.",
    'pro',
    [
      ["Passwords you don't have to remember", 10],
      ['Which kind of MFA actually stops attackers', 10],
      ['Public Wi-Fi, honestly', 10],
      ['Keeping company devices out of the wrong hands', 10],
    ],
  ),
  buildModule(
    'm7',
    7,
    'The first 24 hours after it happens',
    'Stop the money, then call the right people in the right order. What reporting it actually gets you.',
    'pro',
    [
      ['Stop the money first', 9],
      ['Who to call, and in what order', 9],
      ['What reporting it actually gets you', 8],
      ['Talking to your team about what happened', 8],
    ],
  ),
  buildModule(
    'm8',
    8,
    "Leading a team that doesn't get caught",
    "For managers: a payment policy people follow, running your own phishing test, and making it safe to report a mistake.",
    'pro',
    [
      ['A payment policy people actually follow', 8],
      ['Running your own phishing test', 8],
      ['Making it safe to report a mistake', 7],
      ['What to review every quarter', 7],
    ],
  ),
];

export function findModule(id: string): Module | undefined {
  return CURRICULUM.find((m) => m.id === id);
}

export function totalCurriculumDuration(): string {
  const totalMin = CURRICULUM.reduce((sum, m) => sum + m.durationMin, 0);
  return durationLabel(totalMin);
}
